import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Languages } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Small } from '../../ui/typography';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
] as const;

export function LanguageSwitch() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const currentLanguage =
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const handleLanguageSelect = (languageCode: string) => {
    if (languageCode === i18n.language) return;

    i18n.changeLanguage(languageCode);
    // Stay on the current route, swap only the `locale` path param and keep
    // the existing search params. Client-side router navigation (no full
    // page reload) so it behaves the same locally and in production.
    navigate({
      to: '.',
      params: (prev) => ({ ...prev, locale: languageCode }),
      search: (prev) => prev,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='sm'
          className='flex items-center space-y-1 text-(--color-text) hover:bg-rose-300/20 focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-auto py-2'
        >
          <Languages className='w-4 h-4 mr-1' />
          <Small className='flex items-center space-x-1'>
            {currentLanguage.flag}
          </Small>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='start'
        className='min-w-35 bg-white/70 backdrop-blur-md border-(--color-main)/30 shadow-lg'
      >
        {LANGUAGES.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageSelect(language.code)}
            className={`flex items-center space-x-2 py-2 hover:bg-(--color-main)/10 cursor-pointer ${
              i18n.language === language.code ? 'bg-(--color-main)/20' : ''
            }`}
          >
            <Small className='text-lg'>{language.flag}</Small>
            <Small className='font-semibold'>{language.name}</Small>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
